import React, { useEffect, useMemo, useState } from "react";
import { MyColor } from "../../config/myStyle";
import PageBox from "../../layout/PageBox";
import { campaignThemeStore, userStore } from "../../stores";
import { Button, Carousel } from "antd";
import { GetCampaignThemeRes } from "../../api";
import { MyRadio } from "../../components/MyRadio";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getCampaignThemeFallback } from "../../toll/campaignThemeVisual";

const CampaignSelectType = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [selectId, setSelectId] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const themeItems = [...(campaignThemeStore.campaignThemeDataList?.items ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const themeList: GetCampaignThemeRes[][] = [];
    for (let i = 0; i < themeItems.length; i += 3) {
        themeList.push(themeItems.slice(i, i + 3));
    }

    useEffect(() => {
        if (!selectId && themeItems.length) {
            setSelectId(themeItems[0].id ?? '')
        }
    }, [selectId, themeItems])

    useEffect(() => {
        campaignThemeStore.getCampaignThemeList({
            limit: 1000,
            locale: userStore.language,
        })
    }, [])

    // 这里先定义主题名称方法，避免下面 useMemo 提前读取时报初始化错误。
    const getThemeName = (theme?: GetCampaignThemeRes) => {
        if (!theme) return "";
        const locale = userStore.language ?? "en";
        return theme.nameObj?.[locale] ?? String(theme.name);
    };

    const campaignTheme = themeItems.find(item => item.id === selectId);
    const previewImage = useMemo(() => {
        if (!campaignTheme) return undefined;
        return campaignTheme?.campaignThemeAssets?.find(item => item.position === 'background')?.url
            ?? getCampaignThemeFallback(getThemeName(campaignTheme)).cover;
    }, [campaignTheme]);

    const onNext = () => {
        navigate('/campaign/create/form', {
            state: {
                campaignThemeId: selectId
            }
        })
    }


    return (
        <PageBox spinning={campaignThemeStore.getCampaignThemeListStatus.loading}>
            <div className="flex flex-col flex-1 px-[18px] pb-[30px] text-[20px] font-[500]" style={{ color: MyColor.blue }}>
                <div className="mt-[20px] text-center text-[22px] font-[700] leading-[1.15]">
                    {t('campaign_select_type.select_donation_program')}
                </div>

                <Carousel
                    className="w-full pt-[10px]"
                    afterChange={(index) => setCurrentPage(index)}
                    dots={false}
                >
                    {themeList.map((item, index) => (
                        <div key={index}>
                            <div
                                className="grid grid-cols-3 gap-x-[6px] px-[2px]"
                                style={{
                                    minHeight: 138,
                                    borderRadius: 0,
                                    color: "#222",
                                    position: "relative",
                                    margin: "0 auto",
                                }}
                            >
                                {item.map((subItem, subIndex) => (
                                    <button key={subIndex} className="flex flex-col bg-transparent px-[2px] py-[0px]"
                                        style={{ alignItems: "center" }}
                                        onClick={() => setSelectId(subItem.id)}
                                    >
                                        {/* 这页主题区继续使用后台 icon，只按设计稿放大图标与文字。 */}
                                        <img
                                            src={subItem.campaignThemeAssets?.find(item => item.position === 'icon')?.url}
                                            alt=""
                                            style={{ height: 72, width: 72, objectFit: "contain" }} />
                                        <div className="mt-[5px] min-h-[36px] text-center text-[16px] font-[700] leading-[1.05]"
                                            style={{
                                                overflow: "hidden",
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                                color: MyColor.blue,
                                            }}
                                        >
                                            {getThemeName(subItem)}
                                        </div>
                                        {/* 这里把文字到底部选择圆点的有效留白压缩到上一版的大约一半。 */}
                                        <div className="mt-[1px]">
                                            <MyRadio select={selectId === subItem.id} />
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </Carousel>

                {/* 这里调整分页指示器位置和样式，使其贴近主题选择区下方。 */}
                {themeList.length > 1 && (
                    <div className="mt-[10px] flex justify-center gap-[7px]">
                        {themeList.map((_, index) => (
                            <div
                                key={index}
                                className="rounded-full transition-all"
                                style={{
                                    width: currentPage === index ? 20 : 8,
                                    height: 4,
                                    backgroundColor: currentPage === index ? MyColor.blue : "#B8B8B8",
                                }}
                            />
                        ))}
                    </div>
                )}

                <div className="mt-[10px]">
                    {/* 这里让预览封面横向铺满内容区，不再保留左右留白。 */}
                    <div
                        className="mx-[-18px] overflow-hidden"
                        style={{
                            aspectRatio: "16 / 9",
                            backgroundColor: "#F4F1E6",
                            borderRadius: 0,
                        }}
                    >
                        <img
                            src={previewImage}
                            alt=""
                            className="h-full w-full"
                            style={{ objectFit: "cover" }}
                        />
                    </div>

                    <div className="flex flex-col items-center pt-[12px]">
                        <Button
                            type="primary"
                            htmlType="submit"
                            style={{
                                width: 136,
                                borderRadius: 24,
                                background: "#BE9E73",
                                height: 46,
                                fontSize: 17,
                                color: "white",
                                letterSpacing: 1,
                                fontWeight: 500,
                            }}
                            onClick={onNext}
                        >
                            {t('campaign_select_type.next')}
                        </Button>
                    </div>
                </div>
            </div>
        </PageBox>
    );
};

export default observer(CampaignSelectType);
